/**
 * 맥스매치 Lite — 의약품 추가/수정 다이얼로그.
 */
import { useDrugSearchByCode } from '@/entities/drug/hooks/useDrugs';
import type { MaxMatchMiniMr } from '@/entities/max-match/api/max-match.api';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { handleApiError, useToast } from '@/shared/utils/toast';
import React, { useEffect, useState } from 'react';
import { FooterRow } from '@/shared/components/ui/row';

export interface MaxMatchMiniDrugFormValues {
  drugCode: string;
  drugName: string;
  pharmaCompanyName: string;
  price: number;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
}

interface MaxMatchMiniDrugDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refYm: string;
  editingDrug?: MaxMatchMiniMr | null;
  onSubmit: (values: MaxMatchMiniDrugFormValues) => void | Promise<void>;
}

export const MaxMatchMiniDrugDialog: React.FC<MaxMatchMiniDrugDialogProps> = ({
  open,
  onOpenChange,
  refYm,
  editingDrug,
  onSubmit,
}) => {
  const toast = useToast();
  const searchDrug = useDrugSearchByCode();

  const [drugCode, setDrugCode] = useState('');
  const [drugName, setDrugName] = useState('');
  const [pharmaCompanyName, setPharmaCompanyName] = useState('');
  const [price, setPrice] = useState('0');
  const [outQty, setOutQty] = useState('0');
  const [inQty, setInQty] = useState('0');

  useEffect(() => {
    if (!open) return;
    if (editingDrug) {
      setDrugCode(editingDrug.drugCode ?? '');
      setDrugName(editingDrug.drugName ?? '');
      setPharmaCompanyName(editingDrug.pharmaCompanyName ?? '');
      setPrice(String(editingDrug.price ?? 0));
      setOutQty(String(editingDrug.outHospitalQuantity ?? 0));
      setInQty(String(editingDrug.inHospitalQuantity ?? 0));
    } else {
      setDrugCode('');
      setDrugName('');
      setPharmaCompanyName('');
      setPrice('0');
      setOutQty('0');
      setInQty('0');
    }
  }, [open, editingDrug]);

  const handleVerifyDrug = async () => {
    if (!drugCode.trim()) return;
    try {
      const result = await searchDrug.mutateAsync({
        drugCode: drugCode.trim(),
        refYm,
      });
      setDrugName(result.drugName);
      setPharmaCompanyName(result.pharmaCompanyName);
      setPrice(String(result.price));
      toast.success('의약품 정보를 확인했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleSave = async () => {
    if (!drugCode.trim()) {
      toast.error('의약품 코드를 입력해 주세요.');
      return;
    }
    await onSubmit({
      drugCode: drugCode.trim(),
      drugName,
      pharmaCompanyName,
      price: Number(price) || 0,
      outHospitalQuantity: Number(outQty) || 0,
      inHospitalQuantity: Number(inQty) || 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingDrug ? '의약품 수정' : '의약품 추가'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>의약품 코드</Label>
            <div className="flex gap-2">
              <Input
                value={drugCode}
                onChange={(e) => setDrugCode(e.target.value)}
                placeholder="의약품 코드"
              />
              <Button
                type="button"
                className="ruda-button"
                onClick={handleVerifyDrug}
                disabled={!drugCode.trim() || searchDrug.isPending}
              >
                검색
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>의약품 명</Label>
            <Input value={drugName} disabled />
          </div>
          <div className="space-y-2">
            <Label>제약사 명</Label>
            <Input value={pharmaCompanyName} disabled />
          </div>
          <div className="space-y-2">
            <Label>약가</Label>
            <Input value={price} disabled />
          </div>
          <div className="space-y-2">
            <Label>원외 사용량</Label>
            <Input
              type="number"
              step="0.01"
              value={outQty}
              onChange={(e) => setOutQty(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>원내 사용량</Label>
            <Input
              type="number"
              step="0.01"
              value={inQty}
              onChange={(e) => setInQty(e.target.value)}
            />
          </div>
          <FooterRow>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              취소
            </Button>
            <Button onClick={handleSave}>
              {editingDrug ? '수정' : '추가'}
            </Button>
          </FooterRow>
        </div>
      </DialogContent>
    </Dialog>
  );
};
